/* eslint-disable prettier/prettier */
import { MutableRefObject, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { IEMDRContext } from 'context';
import { useEMDR, useScreenSize } from 'hooks';
import { DurationTypeEnum } from 'types';
import { secondsToStringWithHours } from 'utils';
import { AudioType, CountType, MoveType, useAudio, useCount, useMove } from './hooks';

import styles from './EMDRContainer.module.scss';

type EMDRContainerProps = {
  className?: string;
  fullScreen?: boolean;
  size?: number; // ✅ Add size prop
};

export function EMDRContainer({ className, fullScreen = false, size }: EMDRContainerProps) {
  const { t } = useTranslation('providerRoom');
  const { height: screenHeight, width: screenWidth } = useScreenSize();
  const { settings, counterSettings, state, showDurationCountdown }: IEMDRContext = useEMDR();

  const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const canvasRef: RefObject<HTMLCanvasElement> = useRef<HTMLCanvasElement>(null);
  const ctx: MutableRefObject<CanvasRenderingContext2D | null> = useRef<CanvasRenderingContext2D | null>(null);
  const direction = useRef<number>(0);

  const { moveObject, stopMoveObject }: MoveType = useMove({ ctx, canvasRef, containerRef });
  const { updatePassesCountdown }: CountType = useCount();
  const { playSound, stopSound }: AudioType = useAudio();

  const syncCanvasToContainer = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    if (canvas.width !== container.clientWidth || canvas.height !== container.clientHeight) {
      canvas.width = container.clientWidth || 300;
      canvas.height = container.clientHeight || 150;
    }
  }, []);

  const duration: number = useMemo(() => {
    return state === 'play' ? Math.max(100, 3500 - settings.speed * 30) : 0;
  }, [settings, state]);

  useEffect(() => {
    ctx.current = canvasRef.current!.getContext('2d')!;
  }, []);

  useEffect(() => {
    syncCanvasToContainer();
  }, [screenWidth, screenHeight, syncCanvasToContainer]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !canvasRef.current) {
      return;
    }

    const observer = new ResizeObserver(() => {
      syncCanvasToContainer();
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [syncCanvasToContainer]);

  const move = useCallback(() => {
    playSound(duration, direction.current);
    moveObject(duration, direction.current);
  }, [moveObject, playSound, duration]);

  useEffect(() => {
    if (state === 'play') {
      direction.current = 0;
      move();

      const moveObjectInterval = setInterval(() => {
        direction.current = direction.current === 0 ? 1 : 0;

        move();
        updatePassesCountdown();
      }, duration);

      return () => clearInterval(moveObjectInterval);
    } else if (state === 'pause') {
      stopMoveObject();
      stopSound();
    }
  }, [state, duration, move, stopMoveObject, stopSound, updatePassesCountdown]);

  return fullScreen ? (
    <div ref={containerRef} className={styles.container}>
      <canvas width={0} height={0} ref={canvasRef}></canvas>
    </div>
  ) : (
    <div className={className}>
      <Typography variant='body4' component='span' className={styles.whatYourClientSeesLabel}>
        {t('emdrWhatClientSees')}
      </Typography>

      <div ref={containerRef} className={styles.container}>
        <canvas width={0} height={0} ref={canvasRef}></canvas>
      </div>

      {showDurationCountdown && (
        <div className={styles.durationCountdown}>
          {counterSettings.type === DurationTypeEnum.Time && (
            <Typography variant='body4' component='p'>
              {t('emdrTimeLabel')}: {secondsToStringWithHours(counterSettings.time)}
            </Typography>
          )}

          {counterSettings.type === DurationTypeEnum.Passes && (
            <Typography variant='body4' component='p'>
              {t('emdrPassesLabel')}: {counterSettings.passes}
            </Typography>
          )}

          {counterSettings.type === DurationTypeEnum.Manual && (
            <>
              <Typography variant='body4' component='p'>
                {t('emdrTimeLabel')}: {secondsToStringWithHours(counterSettings.time)}
              </Typography>
              <Typography variant='body4' component='p'>
                {t('emdrPassesLabel')}: {counterSettings.passes}
              </Typography>
            </>
          )}
        </div>
      )}
    </div>
  );
}
